import React, {useEffect, useState} from 'react'
import {useIntl} from 'react-intl'
import {useDispatch, useSelector} from 'react-redux'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'
import {ContentHotel} from '@findhotel/sapi/dist/types/packages/core/src'
import {reviewMessages} from 'components/Reviews/ReviewMessages'
import {ReviewsList} from 'components/Reviews/ReviewsList'
import useShowOverlay from 'hooks/useShowOverlay/useShowOverlay'
import {hideOverlay} from 'modules/overlay/slice'
import {OverlayType} from 'modules/overlay/types'
import {getIsNoDatesSearch} from 'modules/search/selectors'

import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {Box} from '@vio/atlas/helpers/Box'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import {linariaMq} from '@vio/atlas/utils/breakpoints'
import {useInView} from '@vio/core/src/_web/utils/browser/hooks/useInView'
import {useDispatchTeamEvent} from '@vio/core/src/analytics/components/TeamContext'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {Action, Category, Entity} from '@vio/core/src/analytics/types/Events'

import {useAiPoweredInsight} from '../AiPoweredReviews/useAiPoweredInsight'
import {ReviewCarouselSection} from './ReviewCarouselSection'
import {ReviewSectionAiPoweredInsights} from './ReviewSectionAiPoweredInsights'
import {ReviewsSectionLoadingState} from './ReviewSectionLoadingState'
import {ReviewSectionRating} from './ReviewSectionRating'
import {ReviewSectionSummary} from './ReviewSectionSummary'
import {getReviewAvailability} from './utils'

const WrapperWithGradient = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${cssTheme.layout.spacing.s500};
  background-image: linear-gradient(
    180deg,
    #faf8f4 0%,
    rgba(250, 248, 244, 0) 100%
  );

  ${linariaMq.desktopXs} {
    background-image: none !important;
  }
  &.--has-padding-top {
    padding-top: ${cssTheme.layout.spacing.s700};
  }
`

const TitleWrapper = styled.div`
  padding: ${cssTheme.layout.spacing.s500};
  padding-top: ${cssTheme.layout.spacing.s700};
  padding-bottom: 0;

  ${linariaMq.desktopSm} {
    padding: 0;
    padding-bottom: ${cssTheme.layout.spacing.s500};
  }
  ${linariaMq.desktopMd} {
    padding: 0;
    padding-bottom: ${cssTheme.layout.spacing.s600};
  }
`

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${cssTheme.layout.spacing.s700};

  ${linariaMq.desktopMd} {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: flex-start;
    gap: ${cssTheme.layout.spacing.s900};
  }
`
const ReviewCarouselWrapper = styled.div`
  margin-top: ${cssTheme.layout.spacing.s700};
  ${linariaMq.desktopMd} {
    padding: 0;
  }
`

interface Props {
  anchorRef: React.RefObject<HTMLDivElement>
  hotel?: ContentHotel
  isFullLayout?: boolean
  mountedOnOverlay?: boolean
}

export const ReviewsSection: React.FC<Props> = ({
  hotel,
  anchorRef,
  isFullLayout = false,
  mountedOnOverlay = false
}) => {
  const dispatch = useDispatch()
  const {isMobile, isDesktopMd} = useDeviceLayout()
  const {formatMessage} = useIntl()
  const dispatchTeamEvent = useDispatchTeamEvent()
  const showOverlay = useShowOverlay()
  const isNoDatesSearch = useSelector(getIsNoDatesSearch)

  const {ref, inView} = useInView({triggerOnce: true, threshold: 0.5})
  const {ref: titleRef, inView: titleInView} = useInView({
    triggerOnce: true,
    threshold: 1
  })

  const hotelId = hotel?.objectID ?? ''

  const {isEnabled: isAiPoweredInsightsEnabled} = useAiPoweredInsight(hotelId)

  const [initialAiPoweredReviewsCategory, setInitialAiPoweredReviewsCategory] =
    useState('')

  const toggleAiPoweredPanel = (category?: string) => {
    if (category) {
      setInitialAiPoweredReviewsCategory(category)
      showOverlay({
        type: OverlayType.AiPoweredReviewsPanel
      })
      return
    }
    dispatch(hideOverlay())
    setInitialAiPoweredReviewsCategory('')
  }

  const handleOpenAllReviews = () => {
    toggleAiPoweredPanel()
    setTimeout(() => {
      showOverlay({
        type: OverlayType.GuestReviews
      })
    }, 100)
  }

  useEffect(() => {
    if (inView) {
      dispatchTeamEvent(
        trackEvent({
          category: Category.User,
          entity: Entity.HotelDetailsReviewsSection,
          action: Action.Viewed
        })
      )
    }
  }, [dispatchTeamEvent, inView])

  useEffect(() => {
    if (titleInView) {
      dispatchTeamEvent(
        trackEvent({
          category: Category.User,
          entity: Entity.HotelDetailsReviewsTitle,
          action: Action.Viewed
        })
      )
    }
  }, [dispatchTeamEvent, titleInView])

  const {hasAnyReviewComponent, hasReviews, hasGuestsTypes, hasSummary} =
    getReviewAvailability(hotel)

  if (!hotel) {
    return (
      <div data-id="reviews-loader">
        <TitleWrapper>
          <Text variant={isMobile ? 'titleL' : 'titleXL'} tag="h2">
            {formatMessage(reviewMessages.guestReviews)}
          </Text>
        </TitleWrapper>
        <Box paddingLeft="s500" paddingRight="s500">
          <ReviewsSectionLoadingState />
        </Box>
      </div>
    )
  }

  if (!hasAnyReviewComponent) return null

  const shouldShowDivider =
    !isMobile &&
    !isDesktopMd &&
    hasGuestsTypes &&
    hasSummary &&
    !isAiPoweredInsightsEnabled

  const title = isNoDatesSearch
    ? formatMessage(reviewMessages.guestReviewsWithHotelName, {
        hotelName: hotel.hotelName
      })
    : formatMessage(reviewMessages.guestReviews)

  return (
    <div data-id="reviews-section" ref={anchorRef}>
      {!mountedOnOverlay && !isMobile && (
        <div ref={titleRef}>
          <TitleWrapper>
            <Text variant={isMobile ? 'titleL' : 'titleXL'} tag="h2">
              {title}
            </Text>
          </TitleWrapper>
        </div>
      )}
      <Wrapper ref={ref}>
        <Box flex="1 1 auto" width="100%">
          <WrapperWithGradient
            className={cx(mountedOnOverlay && isMobile && '--has-padding-top')}
          >
            {isMobile && !mountedOnOverlay && (
              <div ref={titleRef}>
                <TitleWrapper>
                  <Text variant={isMobile ? 'titleL' : 'titleXL'} tag="h2">
                    {title}
                  </Text>
                </TitleWrapper>
              </div>
            )}
            <ReviewSectionRating
              hotel={hotel}
              hasReviews={hasReviews}
              hasSummary={hasSummary}
              isAiPoweredInsightsEnabled={isAiPoweredInsightsEnabled}
            />
            {hasGuestsTypes && (
              <ReviewSectionSummary
                hasDivider={shouldShowDivider}
                guestType={hotel.guestType}
              />
            )}
          </WrapperWithGradient>

          {!isFullLayout && (
            <ReviewCarouselWrapper>
              <ReviewCarouselSection hotelId={hotelId} />
            </ReviewCarouselWrapper>
          )}

          <ReviewSectionAiPoweredInsights
            hotelId={hotel.objectID}
            mountedOnOverlay={mountedOnOverlay}
            initialCategory={initialAiPoweredReviewsCategory}
            onToggle={toggleAiPoweredPanel}
            handleOpenAllReviews={handleOpenAllReviews}
          />
        </Box>
        {isFullLayout && (
          <Box
            flex="1 1 auto"
            paddingLeft="s500"
            paddingRight="s500"
            desktopMd={{padding: 's000'}}
            width="100%"
          >
            <ReviewsList hotelId={hotelId} isOverlay={mountedOnOverlay} />
          </Box>
        )}
      </Wrapper>
    </div>
  )
}
